import { useCallback, useContext, useEffect, useMemo } from "react";

import { Button, Flex } from "@chakra-ui/react";
import { ColumnFiltersState } from "@tanstack/react-table";

import { MultiLegalEntitySelector, MultiRegionSelector } from "@/components/2/data-selectors";
import { LegalEntityContext } from "@/components/2/legal-entity-context";

export type ZoneFiltersType = {
  entities?: string[];
  regions?: string[];
};

type ZoneFiltersProps = {
  columnFilters: ColumnFiltersState;
  setColumnFilters: (filters: ColumnFiltersState) => void;
};

export const ZoneFilters = ({ columnFilters, setColumnFilters }: ZoneFiltersProps) => {
  const legalEntityContext = useContext(LegalEntityContext);

  const entities = useMemo(() => {
    const entityFilter = columnFilters.find(filter => filter.id === "legal_entity_uuid");
    return (entityFilter?.value as string[]) || [];
  }, [columnFilters]);

  const regions = useMemo(() => {
    const regionFilter = columnFilters.find(filter => filter.id === "region_uuid");
    return (regionFilter?.value as string[]) || [];
  }, [columnFilters]);

  const resetFilters = useCallback(() => {
    setColumnFilters([]);
  }, [setColumnFilters]);

  // Auto reset filters when legal entity context changes
  useEffect(() => {
    resetFilters();
    // `legalEntityContext` is the reset trigger, not a value read by the effect.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [legalEntityContext, resetFilters]);

  const updateEntityFilters = (newEntities?: string[]) => {
    const otherFilters = columnFilters.filter(filter => filter.id !== "legal_entity_uuid");
    if (newEntities && newEntities.length > 0) {
      setColumnFilters([...otherFilters, { id: "legal_entity_uuid", value: newEntities }]);
    } else {
      setColumnFilters(otherFilters);
    }
  };

  const updateRegionFilters = (newRegions?: string[]) => {
    const otherFilters = columnFilters.filter(filter => filter.id !== "region_uuid");
    if (newRegions && newRegions.length > 0) {
      setColumnFilters([...otherFilters, { id: "region_uuid", value: newRegions }]);
    } else {
      setColumnFilters(otherFilters);
    }
  };

  const enableResetButton = () => columnFilters.length > 0;

  return (
    <Flex gap={2}>
      {!legalEntityContext && (
        <MultiLegalEntitySelector entities={entities} onChange={updateEntityFilters} />
      )}

      <MultiRegionSelector
        regions={regions}
        onChange={updateRegionFilters}
        legalEntityUuids={
          entities.length > 0 ? entities : legalEntityContext ? [legalEntityContext] : []
        }
      />

      {enableResetButton() && (
        <Button variant="plain" color={"blue"} onClick={resetFilters}>
          Effacer les filtres
        </Button>
      )}
    </Flex>
  );
};
